<template>
  <v-navigation-drawer app fixed permanent clipped>
    <v-list dense class="pt-4">
      <logo width="125" class="pb-2" />
      <v-list-item>
        <v-select
          v-model="currentLocation"
          :items="availableLocations"
          item-text="name"
          return-object
          label="Location"
          dense
          :loading="loading"
          prepend-icon="map"
          no-data-text="No locations available"
          @input="
            if ($router.currentRoute.name === 'bookings')
              $router.push('meeting-rooms');
          "
        ></v-select>
      </v-list-item>
      <v-list-item class="pb-2">
        <v-layout justify-end>
          <add-location v-if="currentLocation" :key="currentLocation.id" edit />
          <v-tooltip v-if="currentLocation && isOwner" right color="primary">
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                class="ml-2 mr-0"
                small
                v-on="on"
                @click="openDeleteDialog()"
              >
                <v-icon color="error">remove_circle</v-icon>
              </v-btn>
            </template>
            <span>Delete location</span>
          </v-tooltip>
          <add-location v-if="isOwner" />
        </v-layout>
      </v-list-item>
      <v-list-item
        to="/referral-codes"
        active-class="error--text"
        class="mt-1 mb-3"
      >
        <v-list-item-action> <v-icon>loyalty</v-icon> </v-list-item-action>
        <v-list-item-content>
          <v-list-item-title>Referral Codes</v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-divider class="mb-3"></v-divider>
      <template v-if="currentLocation">
        <v-list-item
          v-for="item in allowedNavItems"
          :key="item.route"
          :to="`/${item.route}`"
          active-class="error--text"
        >
          <v-list-item-action>
            <v-icon>{{ item.icon }}</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-list>
    <v-dialog v-model="deleteDialog" persistent max-width="290">
      <v-card>
        <v-app-bar flat dark color="primary">
          <v-toolbar-title>Are you sure?</v-toolbar-title>
        </v-app-bar>
        <v-card-text>
          If you delete this location it will be gone forever, along with any
          rooms or messages associated with it.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteLocation()">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
import Logo from '@/components/Logo.vue';
import AddLocation from '@/components/AddLocation';
import { locationsCollection, adminsCollection } from '../firebaseConfig.js';

export default {
  name: 'NavDrawer',
  components: {
    Logo,
    AddLocation
  },
  data() {
    return {
      locations: [],
      adminLocations: [],
      navItems: [
        { title: 'Meeting Rooms', icon: 'room', route: 'meeting-rooms' },
        { title: 'Message Board', icon: 'message', route: 'message-board' },
        { title: 'Profiles', icon: 'people', route: 'profiles' },
        { title: 'Admins', icon: 'vpn_key', route: 'admins', owner: true }
      ],
      deleteDialog: false,
      loading: false
    };
  },
  computed: {
    currentLocation: {
      get() {
        return this.$store.state.currentLocation;
      },
      set(value) {
        this.$store.commit('setCurrentLocation', value);
      }
    },
    currentUserId() {
      if (this.$store.state.currentUser) {
        return this.$store.state.currentUser.uid;
      } else {
        return null;
      }
    },
    isOwner() {
      return this.$store.state.isOwner;
    },
    isAdmin() {
      return this.$store.state.isAdmin;
    },
    allowedNavItems() {
      if (this.isOwner) {
        return this.navItems;
      } else {
        return this.navItems.filter(item => {
          return !item.owner;
        });
      }
    },
    availableLocations() {
      if (this.isOwner) {
        return this.locations;
      } else {
        return this.locations.filter(location =>
          this.adminLocations.includes(location.id)
        );
      }
    }
  },
  mounted() {
    if (this.isOwner || this.isAdmin) {
      this.getAdminLocations();
    }
  },
  methods: {
    getAdminLocations() {
      this.loading = true;
      adminsCollection
        .doc(this.currentUserId)
        .get()
        .then(res => {
          this.adminLocations = res.data().locations;
        })
        .catch(error => {
          this.$store.dispatch('createSnackbar', {
            color: 'error',
            text: `Error fetching admin locations: ${error}`
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    openDeleteDialog() {
      this.deleteDialog = true;
    },
    deleteLocation() {
      locationsCollection
        .doc(this.currentLocation.id)
        .delete()
        .then(() => {
          this.deleteDialog = false;
          this.$store.dispatch('createSnackbar', {
            color: 'success',
            text: `Location deleted successfully: ${this.currentLocation.name}`
          });
          this.currentLocation = null;
        })
        .catch(error => {
          this.deleteDialog = false;
          this.$store.dispatch('createSnackbar', {
            color: 'error',
            text: `Error deleting location: ${error}`
          });
        });
    }
  },
  firestore: {
    locations: locationsCollection.orderBy('name')
  }
};
</script>

<style lang="scss">
.v-list__tile--active {
  background-color: rgba(0, 0, 0, 0.09);
}
</style>
